import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { AuthContext } from '../App';

// Cross-site "my reports" status board (task 12): at a glance, which clients'
// monthly reports were already sent, which are ready but not sent, and which
// are not finished yet.

var MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

function formatMonth(m) {
  if (!m) return '—';
  var p = String(m).split('-');
  if (p.length !== 2) return m;
  return MONTHS_HE[parseInt(p[1], 10)] + ' ' + p[0];
}

var STATE_META = {
  sent:    { label: 'נשלח',       color: '#0D9F6E', bg: 'rgba(13,159,110,0.12)' },
  ready:   { label: 'מוכן, לא נשלח', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  pending: { label: 'לא הושלם',   color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  none:    { label: 'אין דוח',    color: '#94A3B8', bg: 'rgba(148,163,184,0.14)' },
};

function StateBadge(props) {
  var m = STATE_META[props.state] || STATE_META.none;
  return (
    <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '12px',
      fontSize: '12px', fontWeight: 700, color: m.color, background: m.bg }}>{m.label}</span>
  );
}

export default function ReportsOverview() {
  var navigate = useNavigate();
  var auth = useContext(AuthContext);
  var [rows, setRows] = useState(null);
  var [error, setError] = useState(null);
  var [search, setSearch] = useState('');
  var [stateFilter, setStateFilter] = useState('');
  var [myOnly, setMyOnly] = useState(false);

  useEffect(function() {
    api.getReportsOverview()
      .then(function(d) { setRows(d || []); })
      .catch(function(err) { setError(err.message); });
  }, []);

  if (error) return <div className="loading">שגיאה בטעינת נתונים: {error}</div>;
  if (!rows) return <div className="loading">טוען...</div>;

  // Only surface active sites in the board by default; paused/frozen add noise.
  var base = rows.filter(function(r) { return r.site_status === 'active'; });

  var counts = {
    all: base.length,
    sent: base.filter(function(r) { return r.state === 'sent'; }).length,
    ready: base.filter(function(r) { return r.state === 'ready'; }).length,
    unfinished: base.filter(function(r) { return r.state === 'pending' || r.state === 'none'; }).length,
  };

  var filtered = base;
  if (myOnly && auth && auth.user) {
    filtered = filtered.filter(function(r) { return r.assigned_to === auth.user.name; });
  }
  if (search) {
    var q = search.toLowerCase();
    filtered = filtered.filter(function(r) { return (r.site_name || '').toLowerCase().includes(q); });
  }
  if (stateFilter === 'sent') filtered = filtered.filter(function(r) { return r.state === 'sent'; });
  else if (stateFilter === 'ready') filtered = filtered.filter(function(r) { return r.state === 'ready'; });
  else if (stateFilter === 'unfinished') filtered = filtered.filter(function(r) { return r.state === 'pending' || r.state === 'none'; });

  var chips = [
    { key: '', label: 'הכל (' + counts.all + ')' },
    { key: 'sent', label: 'נשלחו (' + counts.sent + ')' },
    { key: 'ready', label: 'לא נשלחו (' + counts.ready + ')' },
    { key: 'unfinished', label: 'לא הושלמו (' + counts.unfinished + ')' },
  ];

  return (
    <>
      <div style={{ margin: '4px 2px 20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)' }}>הדוחות שלי</h1>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>
          סטטוס הדוח האחרון לכל לקוח — מה נשלח, מה מוכן, ומה עדיין לא הושלם
        </div>
      </div>

      <div className="filter-row">
        <div className="topbar-search" style={{ width: '300px' }}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/>
          </svg>
          <input type="text" placeholder="חיפוש לקוח..." value={search}
            onChange={function(e) { setSearch(e.target.value); }} />
        </div>

        {chips.map(function(c) {
          return (
            <div key={c.key} className={'filter-chip ' + (stateFilter === c.key ? 'active' : '')}
              onClick={function() { setStateFilter(c.key); }}>{c.label}</div>
          );
        })}

        {auth && auth.user && (
          <>
            <div className="filter-divider" />
            <div className={'filter-chip ' + (myOnly ? 'active' : '')}
              onClick={function() { setMyOnly(!myOnly); }}
              style={myOnly ? { background: 'rgba(59,130,246,0.15)', borderColor: 'var(--accent)', color: 'var(--accent)' } : {}}
            >האתרים שלי</div>
          </>
        )}
      </div>

      <div className="sites-table-wrapper animate-in">
        <table className="sites-table">
          <thead>
            <tr>
              <th>לקוח</th>
              <th>מקדם</th>
              <th>חודש אחרון</th>
              <th>סטטוס</th>
              <th>נשלח בתאריך</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px' }}>אין דוחות להצגה</td></tr>
            )}
            {filtered.map(function(r) {
              var rep = r.report;
              var sentAt = (rep && rep.sent_at) ? new Date(rep.sent_at).toLocaleDateString('he-IL') : '—';
              return (
                <tr key={r.site_id}>
                  <td style={{ fontWeight: 600 }}>{r.site_name}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{r.assigned_to || '—'}</td>
                  <td>{formatMonth(r.month)}</td>
                  <td><StateBadge state={r.state} /></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{sentAt}</td>
                  <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {rep && rep.public_url && (
                      <a href={rep.public_url} target="_blank" rel="noreferrer"
                        className="btn-sm" style={{ marginLeft: '8px' }}>דוח אונליין</a>
                    )}
                    <button className="btn-sm primary"
                      onClick={function() { navigate('/sites/' + r.site_id + '/reports'); }}>
                      פתח
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
